import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Code2, LayoutDashboard, Smartphone, Globe, Network, Package, ArrowUpRight, Check, X } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import Reveal from "@/components/Reveal";

const ICONS = {
  "software-a-medida": Code2,
  "erp-crm": LayoutDashboard,
  "web-apps-pwa": Smartphone,
  "desarrollo-web": Globe,
  "integracion-adaptacion": Network,
  "software-empresarial": Package,
};

const Services = () => {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);

  return (
    <section id="servicios" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="services-section">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.28em] text-cy">{t.services.label}</p>
        <h2 className="mt-4 max-w-3xl font-display text-2xl font-bold leading-[1.15] tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
          {t.services.title}
        </h2>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-400">{t.services.sub}</p>
      </Reveal>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {t.services.items.map((s, i) => {
          const Icon = ICONS[s.id];
          return (
            <Reveal key={s.id} delay={i * 0.07} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <motion.button
                data-testid={`service-card-${s.id}`}
                onClick={() => setActive(s)}
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 22 }}
                className="group relative flex h-full w-full flex-col overflow-hidden rounded-xl border border-cy/10 bg-qcard p-6 text-left transition-colors duration-300 hover:border-cy/40 hover:bg-qcardhover sm:p-8"
              >
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-cy/0 blur-3xl transition-all duration-500 group-hover:bg-cy/15" />
                <div className="flex items-start justify-between">
                  <span className="grid h-12 w-12 place-items-center rounded-lg border border-cy/20 bg-ink/60">
                    <Icon className="h-5 w-5 text-cy" strokeWidth={1.6} />
                  </span>
                  <span className="font-mono text-xs tracking-[0.2em] text-slate-600">{s.num}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-slate-50 sm:text-2xl">{s.title}</h3>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-em">{s.tagline}</p>
                <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{s.summary}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.18em] text-cy">
                  {t.services.details}
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </motion.button>
            </Reveal>
          );
        })}
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            key="svc-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/80 p-4 backdrop-blur-md"
            onClick={() => setActive(null)}
            data-testid="service-modal-overlay"
          >
            <motion.div
              initial={{ opacity: 0, y: 48, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 32, scale: 0.97 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-cy/25 bg-panel p-8 shadow-[0_0_80px_rgba(0,240,255,0.15)] sm:p-10"
              data-testid="service-detail-modal"
            >
              <button
                data-testid="service-modal-close"
                onClick={() => setActive(null)}
                className="absolute right-5 top-5 grid h-9 w-9 place-items-center rounded-full border border-cy/20 text-slate-400 transition-colors hover:border-cy hover:text-cy"
                aria-label={t.services.close}
              >
                <X className="h-4 w-4" />
              </button>
              <span className="font-mono text-xs tracking-[0.25em] text-cy">{active.num} // {active.id.toUpperCase()}</span>
              <h3 className="mt-3 font-display text-2xl font-bold text-slate-50 sm:text-3xl">{active.title}</h3>
              <p className="mt-2 font-mono text-xs uppercase tracking-[0.14em] text-em">{active.tagline}</p>
              <p className="mt-5 text-sm leading-relaxed text-slate-400">{active.summary}</p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {active.features.map((f, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.15 + i * 0.05 }}
                    className="flex items-start gap-3 text-sm text-slate-300"
                  >
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-cy" />
                    {f}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Services;
